import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Button,
  SimpleGrid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
} from "@chakra-ui/react";
import { HiOutlineCalendarDays, HiChevronLeft, HiChevronRight } from "react-icons/hi2";

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Values are plain "YYYY-MM-DD" strings (same contract as <input type="date">),
// parsed/formatted locally so a timezone offset can never shift the day.
const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fromYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const formatDMY = (s) => {
  const d = fromYMD(s);
  if (!d) return "";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const sameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Themed date picker. `p` is a literal palette object (PALETTES.light/dark) so
 * it renders correctly inside modals/portals where CSS variables don't resolve.
 */
const ClassroomDatePicker = ({ value, onChange, p, placeholder = "Select a date", disablePast = true, width = "193px" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const selected = fromYMD(value);
  const today = useMemo(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }, []);
  const [view, setView] = useState(() => {
    const base = selected || today;
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });

  const cells = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const daysInMonth = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
    const offset = (first.getDay() + 6) % 7; // Monday-first
    const out = [];
    for (let i = 0; i < offset; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(view.getFullYear(), view.getMonth(), d));
    return out;
  }, [view]);

  const shiftMonth = (delta) => setView((v) => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const pick = (date) => {
    onChange(toYMD(date));
    onClose();
  };

  const navBtn = {
    as: "button",
    type: "button",
    alignItems: "center",
    justifyContent: "center",
    boxSize: "26px",
    borderRadius: "8px",
    _hover: { background: p.hover },
  };

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start" gutter={6}>
      <PopoverTrigger>
        <Flex
          as="button"
          type="button"
          align="center"
          gap="8px"
          width={width}
          height="38px"
          px="12px"
          borderRadius="8px"
          border="1px solid"
          borderColor={isOpen ? p.accent : p.inputBorder}
          background={p.inputBg}
          boxShadow={isOpen ? `0 0 0 1px ${p.accent}` : "none"}
          transition="all 0.15s"
          _hover={{ borderColor: isOpen ? p.accent : p.textMuted }}
        >
          <Icon as={HiOutlineCalendarDays} boxSize="16px" color={p.textSub} flexShrink={0} />
          <Text fontSize="13px" color={value ? p.text : p.textMuted} noOfLines={1}>
            {value ? formatDMY(value) : placeholder}
          </Text>
        </Flex>
      </PopoverTrigger>

      <PopoverContent
        width="272px"
        background={p.surface}
        border="1px solid"
        borderColor={p.border}
        borderRadius="12px"
        boxShadow="0 20px 48px -18px rgba(0,0,0,0.5)"
        _focus={{ outline: "none", boxShadow: "0 20px 48px -18px rgba(0,0,0,0.5)" }}
      >
        <PopoverBody padding="12px">
          {/* Month navigation */}
          <Flex align="center" justify="space-between" mb="10px">
            <Flex {...navBtn} onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <Icon as={HiChevronLeft} boxSize="16px" color={p.textSub} />
            </Flex>
            <Text fontSize="13px" fontWeight="600" color={p.text}>
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </Text>
            <Flex {...navBtn} onClick={() => shiftMonth(1)} aria-label="Next month">
              <Icon as={HiChevronRight} boxSize="16px" color={p.textSub} />
            </Flex>
          </Flex>

          {/* Weekday header */}
          <SimpleGrid columns={7} spacing="2px" mb="4px">
            {WEEKDAYS.map((w) => (
              <Flex key={w} align="center" justify="center" height="26px">
                <Text fontSize="10.5px" fontWeight="600" color={p.textMuted}>{w}</Text>
              </Flex>
            ))}
          </SimpleGrid>

          {/* Day grid */}
          <SimpleGrid columns={7} spacing="2px">
            {cells.map((date, i) => {
              if (!date) return <Box key={`b${i}`} height="32px" />;
              const isSelected = sameDay(date, selected);
              const isToday = sameDay(date, today);
              const disabled = disablePast && date < today;
              return (
                <Flex
                  key={date.toISOString()}
                  as="button"
                  type="button"
                  align="center"
                  justify="center"
                  height="32px"
                  borderRadius="8px"
                  cursor={disabled ? "not-allowed" : "pointer"}
                  opacity={disabled ? 0.35 : 1}
                  background={isSelected ? p.accent : "transparent"}
                  border={!isSelected && isToday ? "1px solid" : "none"}
                  borderColor={p.accent}
                  transition="background 0.12s"
                  _hover={!disabled && !isSelected ? { background: p.hover } : {}}
                  onClick={() => !disabled && pick(date)}
                  disabled={disabled}
                >
                  <Text
                    fontSize="12.5px"
                    fontWeight={isSelected || isToday ? "600" : "400"}
                    color={isSelected ? p.onAccent : p.text}
                  >
                    {date.getDate()}
                  </Text>
                </Flex>
              );
            })}
          </SimpleGrid>

          {/* Footer actions */}
          <Flex justify="space-between" align="center" mt="10px" pt="10px" borderTop="1px solid" borderColor={p.border}>
            <Button
              variant="ghost"
              size="xs"
              fontSize="12px"
              fontWeight="500"
              color={p.textSub}
              _hover={{ bg: p.hover }}
              onClick={() => { onChange(""); onClose(); }}
            >
              Clear
            </Button>
            <Button
              variant="ghost"
              size="xs"
              fontSize="12px"
              fontWeight="600"
              color={p.accent}
              _hover={{ bg: p.hover }}
              onClick={() => pick(today)}
            >
              Today
            </Button>
          </Flex>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default ClassroomDatePicker;
